import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Tabs,
  Tab,
  Grid,
} from "@mui/material";
import { Add, Warning, ErrorOutline } from "@mui/icons-material";
import CalibrationHistory from "./Tabs/CalibrationHistory";
import UploadCertificates from "./Tabs/UploadCertificates";
import ActivityLog from "./Tabs/ActivityLog";
import EquipmentFilters from "./Filters/EquipmentFilters";
import NewEquipmentButton from "./Buttons/NewEquipmentButton";
import NewCalibrationLogButton from "./Buttons/NewCalibrationLogButton";
import OverdueCalibrationsButton from "./Buttons/OverdueCalibrationsButton";
import FailedCalibrationsButton from "./Buttons/FailedCalibrationsButton";
import Loader from "../common/Loader";

const departments = ["All", "Production", "Quality", "Design"];
const statuses = ["All", "Calibrated", "Due", "Overdue", "Failed"];

export default function EquipmentCalibrationDashboard() {
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    department: "All",
    type: "All",
  });
  const [equipmentList, setEquipmentList] = useState([
    { id: 1, name: "EQ1", type: "Measurement", department: "Quality" },
    { id: 2, name: "EQ2", type: "Printer", department: "Production" },
    { id: 3, name: "EQ3", type: "Sensor", department: "Design" },
  ]);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [searchText, setSearchText] = useState("");

  const handleFilterChange = (field) => (value) => {
    setFilters({ ...filters, [field]: value });
  };

  const filteredEquipment = equipmentList.filter((eq) => {
    const search = searchText.toLowerCase();
    const matchesSearch =
      eq.name.toLowerCase().includes(search) ||
      eq.type.toLowerCase().includes(search);
    const matchesDepartment =
      filters.department === "All" || eq.department === filters.department;
    const matchesType = filters.type === "All" || eq.type === filters.type;

    return matchesSearch && matchesDepartment && matchesType;
  });

  useEffect(() => {
    // Simulate loading data
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return <Loader />;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Paper sx={{ p: 1, mb: 1, boxShadow: "none", border: "none" }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h5" fontWeight="bold" sx={{ color: "#4F46E5" }}>
            Equipment Calibration Dashboard
          </Typography>

          <Stack direction="row" spacing={1}>
            <NewEquipmentButton />
            <NewCalibrationLogButton />
            <OverdueCalibrationsButton />
            <FailedCalibrationsButton />
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={2}>
        {/* Left Panel: Equipment List + Filters */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <EquipmentFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              searchText={searchText}
              onSearchTextChange={setSearchText}
              departments={departments}
            />

            {/* List */}
            <List dense>
              {filteredEquipment.length > 0 ? (
                filteredEquipment.map((eq) => (
                  <ListItem
                    button
                    selected={selectedEquipment?.id === eq.id}
                    key={eq.id}
                    onClick={() => setSelectedEquipment(eq)}
                  >
                    <ListItemText
                      primary={eq.name}
                      secondary={`${eq.type} | ${eq.department}`}
                    />
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText primary="No equipment found." />
                </ListItem>
              )}
            </List>
          </Paper>
        </Grid>

        {/* Right Panel: Tabbed View */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              {selectedEquipment?.name || "Equipment Details"}
            </Typography>

            <Tabs
              value={tabIndex}
              onChange={(e, newIndex) => setTabIndex(newIndex)}
              sx={{ mb: 2 }}
            >
              <Tab label="Details & History" />
              <Tab label="Uploads & Certificates" />
              <Tab label="Timeline / Activity Log" />
            </Tabs>
            <Divider sx={{ mb: 2 }} />

            {tabIndex === 0 && (
              <CalibrationHistory
                selectedEquipment={
                  selectedEquipment || { id: 0, name: "No Equipment Selected" }
                }
              />
            )}
            {tabIndex === 1 && (
              <UploadCertificates
                selectedEquipment={
                  selectedEquipment || { id: 0, name: "No Equipment Selected" }
                }
              />
            )}
            {tabIndex === 2 && (
              <ActivityLog
                selectedEquipment={
                  selectedEquipment || { id: 0, name: "No Equipment Selected" }
                }
              />
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
